function patientData(practitionerID, itemsPerPage, deleteScriptIDinDJ, getNewUserScriptIDinDJ, addUserIDinDJ, copyScriptIDinDJ) {
  return {
    _initialized: false,
    practitionerID: practitionerID ? `gid://shopify/Customer/${practitionerID}` : null,
    patients: [],
    filteredPatientsData: [],
    searchQuery: '',
    previousSearchQuery: '',
    filterStatus: 'all',
    sortBy: 'created',
    sortOrder: 'desc',
    currentPage: 1,
    itemsPerPage: itemsPerPage || 6,
    showPatientForm: false,
    showPatientFormSuccess: false,
    successTimeoutId: null,
    termsPatientForm: false,
    pagesToShow: 5,
    addUserIDinDJ: addUserIDinDJ || '',
    isAwaitingResponse: false,
    isLoadingAllPages: false,
    deleteScriptIDinDJ: deleteScriptIDinDJ || '',
    copyScriptIDinDJ: copyScriptIDinDJ || '',
    getNewUserScriptIDinDJ: getNewUserScriptIDinDJ || '',
    notification: {
      message: '',
      status: 'ok',
    },
    isUrlSuccessParam: false,
    copiedProtocolProductsMap: {},

    normalizeDate(dateString) {
      const isFormattedDate = /^\d{2}\/\d{2}\/\d{4}$/.test(dateString);
      if (isFormattedDate) {
        return dateString;
      }
      return this.formatDate(dateString);
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    },

    removeURLParam(param) {
      const url = new URL(window.location);
      const urlParams = new URLSearchParams(url.search);

      if (urlParams.has(param)) {
        urlParams.delete(param);
        window.history.replaceState(
          {},
          document.title,
          `${url.pathname}?${urlParams}`
        );

        this.isUrlSuccessParam = false;
      }
    },

    isURLParamsSuccess() {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('patient_success')) {
        this.showPatientForm = true;
        this.showPatientFormSuccess = true;
        this.isUrlSuccessParam = true;

        this.successTimeoutId = setTimeout(() => {
          if (this.successTimeoutId) {
            this.showPatientForm = false;
            this.showPatientFormSuccess = false;

            this.getNewUser();

            this.removeURLParam('patient_success');

            this.successTimeoutId = null;
          }
        }, 10000);
      }
    },

    initData() {
      // Prevent duplicate initialization
      if (this._initialized) {
        return;
      }
      this._initialized = true;

      const practitionerID = this.practitionerID?.split('/').pop();
      if (!practitionerID) {
        console.error('No practitioner ID available');
        return;
      }

      const localStorageKey = `protocol_dashboard_data_${practitionerID}`;
      
      // Check localStorage first
      const cachedData = localStorage.getItem(localStorageKey);
      if (cachedData) {
        try {
          const parsedCache = JSON.parse(cachedData);
          this.patients = parsedCache;
          
          // Show dashboard immediately with cached data
          setTimeout(() => {
            const dashboard = document.querySelector('.patients__dashboard');
            if (dashboard) {
              dashboard.classList.remove('hidden');
            }
          }, 100);
        } catch (e) {
          console.error('Error parsing cached data:', e);
        }
      }

      const patientsScript = document.getElementById('PROTOCOL_DATA');
      if (patientsScript) {
        const rawPatients = JSON.parse(patientsScript.textContent);
        const freshData = rawPatients.map(patient => ({
          ...patient,
          title: patient.title || patient.patient_name || patient.name || 'Unknown',
          raw_date: patient.created || '',
          created: patient.created ? this.normalizeDate(patient.created) : '',
          email: patient.email || ''
        }));
        
        // If no cache, use fresh data
        if (!cachedData) {
          this.patients = freshData;
        }
      }

      // Check pagination info and load all pages if needed
      this.checkAndLoadAllPages(localStorageKey, !cachedData);

      this.isURLParamsSuccess();
    },

    async checkAndLoadAllPages(localStorageKey, isFirstLoad = false) {
      const paginationScript = document.getElementById('PROTOCOL_PAGINATION_INFO');
      if (!paginationScript) return;

      const paginationInfo = JSON.parse(paginationScript.textContent);
      const { totalPages, nextUrl, sectionId } = paginationInfo;

      // If only 1 page, compare and save to localStorage
      if (totalPages <= 1 || !nextUrl) {
        // Get fresh page 1 data
        const patientsScript = document.getElementById('PROTOCOL_DATA');
        if (patientsScript) {
          const rawPatients = JSON.parse(patientsScript.textContent);
          const freshData = rawPatients.map(patient => ({
            ...patient,
            title: patient.title || patient.patient_name || patient.name || 'Unknown',
            raw_date: patient.created || '',
            created: patient.created ? this.normalizeDate(patient.created) : '',
            email: patient.email || ''
          }));
          
          // Compare current data with fresh data
          const currentSnapshot = JSON.stringify(this.patients);
          const freshSnapshot = JSON.stringify(freshData);
          
          // Only update if data has changed or this is first load
          if (isFirstLoad || currentSnapshot !== freshSnapshot) {
            this.patients = freshData;
          }
          
          // Always save to localStorage to keep it fresh
          if (localStorageKey) {
            localStorage.setItem(localStorageKey, JSON.stringify(freshData));
          }
        }
        
        // Wait for Alpine to render if needed
        setTimeout(() => {
          const dashboard = document.querySelector('.patients__dashboard');
          if (dashboard) {
            dashboard.classList.remove('hidden');
          }
        }, isFirstLoad ? 100 : 0);
        return;
      }

      // If first load with multiple pages, show dashboard immediately with page 1 data
      if (isFirstLoad) {
        // Wait for Alpine to render the dashboard (x-if needs time)
        setTimeout(() => {
          const dashboard = document.querySelector('.patients__dashboard');
          if (dashboard) {
            dashboard.classList.remove('hidden');
          }
        }, 100);
      }

      // Store flag to prevent duplicate loads
      if (window._protocolAllPagesLoaded) return;
      
      // Load remaining pages on first user interaction (click, scroll, or after 2 seconds)
      const loadRemainingPages = async () => {
        if (window._protocolAllPagesLoaded) return;
        window._protocolAllPagesLoaded = true;

        this.isLoadingAllPages = true;
        this.isAwaitingResponse = true;

        const initialPatientsSnapshot = JSON.stringify(this.patients);

        try {
          let currentNextUrl = nextUrl;
          const allFreshPatients = []; // Collect ALL fresh data from scratch
          
          // If we have cached data, we need to load ALL pages (including page 1) to compare
          // Add page 1 data first
          const patientsScript = document.getElementById('PROTOCOL_DATA');
          if (patientsScript) {
            const rawPatients = JSON.parse(patientsScript.textContent);
            const page1Data = rawPatients.map(patient => ({
              ...patient,
              title: patient.title || patient.patient_name || patient.name || 'Unknown',
              raw_date: patient.created || '',
              created: patient.created ? this.normalizeDate(patient.created) : '',
              email: patient.email || ''
            }));
            allFreshPatients.push(...page1Data);
          }
          
          // Load all remaining pages using paginate.next.url
          while (currentNextUrl) {
            const pageUrl = `${currentNextUrl}&sections=${sectionId}`;
            const response = await fetch(pageUrl);
            const data = await response.json();
            
            // Extract the section HTML
            const sectionHtml = data[sectionId];
            
            // Parse the HTML to extract JSON data
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHtml, 'text/html');
            const pageDataScript = doc.querySelector('[id^="PROTOCOL_PAGE_DATA_"]');
            
            if (pageDataScript) {
              const pageData = JSON.parse(pageDataScript.textContent);
              
              // Collect items without updating patients yet
              const newPatients = pageData.items.map(patient => ({
                ...patient,
                title: patient.title || patient.patient_name || patient.name || 'Unknown',
                raw_date: patient.created || '',
                created: patient.created ? this.normalizeDate(patient.created) : '',
                email: patient.email || ''
              }));
              
              allFreshPatients.push(...newPatients);
              
              // Get next URL for next iteration
              currentNextUrl = pageData.nextUrl;
            } else {
              break;
            }
          }
          
          // Now we have all fresh data, compare with current data
          const allFreshPatientsSnapshot = JSON.stringify(allFreshPatients);
          const dataChanged = initialPatientsSnapshot !== allFreshPatientsSnapshot;
          
          // Only update if data has changed or this is first load
          if (isFirstLoad || dataChanged) {
            this.patients = allFreshPatients;
          }
          
          // Save to localStorage
          if (localStorageKey) {
            localStorage.setItem(localStorageKey, JSON.stringify(allFreshPatients));
          }
        } catch (error) {
          console.error('Error loading additional pages:', error);
        } finally {
          this.isLoadingAllPages = false;
          this.isAwaitingResponse = false;
          
          // Remove hidden class from dashboard after all pages loaded (if not already shown)
          if (!isFirstLoad) {
            const dashboard = document.querySelector('.patients__dashboard');
            if (dashboard) {
              dashboard.classList.remove('hidden');
            }
          }
        }
      };

      // Trigger loading on first interaction or after 2 seconds
      let interactionOccurred = false;
      
      const onInteraction = () => {
        if (!interactionOccurred) {
          interactionOccurred = true;
          loadRemainingPages();
          // Remove listeners after first interaction
          document.removeEventListener('click', onInteraction);
          document.removeEventListener('scroll', onInteraction);
          document.removeEventListener('keydown', onInteraction);
        }
      };

      document.addEventListener('click', onInteraction, { once: true });
      document.addEventListener('scroll', onInteraction, { once: true });
      document.addEventListener('keydown', onInteraction, { once: true });

      // Also load after 2 seconds as fallback
      setTimeout(() => {
        if (!interactionOccurred) {
          interactionOccurred = true;
          loadRemainingPages();
        }
      }, 2000);
    },

    formatSortBy() {
      if (this.sortBy === 'name') {
        return 'Titre';
      } else if (this.sortBy === 'created') {
        return 'Créé';
      }
      return 'Unknown';
    },

    formatSortOrder() {
      if (this.sortOrder === 'asc') {
        return this.sortBy === 'name' ? 'A-Z' : 'Ancien-Nouveau';
      } else if (this.sortOrder === 'desc') {
        return this.sortBy === 'name' ? 'Z-A' : 'Nouveau-Ancien';
      }
      return 'Unknown';
    },

    get totalPages() {
      return Math.ceil(this.filteredPatientsData.length / this.itemsPerPage);
    },
    setStatusFilter(status) {
      this.filterStatus = status;
      this.currentPage = 1;
    },
    get filteredPatients() {
      let query = this.searchQuery.toLowerCase().trim();
      
      let filtered = this.patients.filter(p => 
        !query || p.title?.toLowerCase().includes(query) || p.email?.toLowerCase().includes(query)
      );

      filtered.sort((a, b) => {
        // PRIORITY 1: Recently copied items always go first
        const aCopied = a._copiedAt || 0;
        const bCopied = b._copiedAt || 0;
        
        if (aCopied && bCopied) {
          // Both are recently copied - newer one first
          return bCopied - aCopied;
        }
        if (aCopied) return -1; // a is recently copied, goes first
        if (bCopied) return 1;  // b is recently copied, goes first
        
        // PRIORITY 2: Regular sorting
        let field = this.sortBy;
        
        if (field === 'created' || field === 'register_date') {
          let timeA = new Date(a.raw_date).getTime() || 0;
          let timeB = new Date(b.raw_date).getTime() || 0;
          return this.sortOrder === 'asc' ? timeA - timeB : timeB - timeA;
        }

        let valA = a.title?.toLowerCase() || '';
        let valB = b.title?.toLowerCase() || '';
        return this.sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      });

      // Update filteredPatientsData for count display
      this.filteredPatientsData = filtered;
      return filtered;
    },

    get paginatedPatients() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredPatients.slice(start, start + this.itemsPerPage);
    },

    get visiblePages() {
      const half = Math.floor(this.pagesToShow / 2);
      let start = this.currentPage - half;
      let end = this.currentPage + half;

      if (start < 1) {
        start = 1;
        end = Math.min(this.pagesToShow, this.totalPages);
      }

      if (end > this.totalPages) {
        end = this.totalPages;
        start = Math.max(1, this.totalPages - this.pagesToShow + 1);
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    sort(field, order = null) {
      if (order) {
        this.sortOrder = order;
      } else {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      }

      this.sortBy = field;
      this.currentPage = 1; // Reset to first page when sorting
      
      // Clear recently copied flags when user manually changes sort
      this.patients.forEach(p => delete p._copiedAt);
    },

    searchNow() {
      // Reset to first page when searching
      this.currentPage = 1;
    },

    clearSuccessTimeout() {
      if (this.successTimeoutId) {
        clearTimeout(this.successTimeoutId);
        this.successTimeoutId = null;
      }
    },

    closePatientForm() {
      this.getNewUser();

      this.clearSuccessTimeout();

      if (this.showPatientForm) {
        this.showPatientForm = false;
      }

      if (this.showPatientFormSuccess) {
        this.showPatientFormSuccess = false;
      }
    },

    openPatientForm() {
      this.clearSuccessTimeout();

      this.showPatientForm = true;
    },

    showNotification(notification, timeout = 5000) {
      this.notification = { ...notification };

      setTimeout(() => {
        this.clearNotification();
      }, timeout);
    },

    clearNotification() {
      this.notification = {
        message: '',
        status: 'ok',
      };
    },

    async getNewUser() {
      if (this.isUrlSuccessParam) {
        this.isAwaitingResponse = true;

        const taskId = this.getNewUserScriptIDinDJ;

        const url = `/apps/datajet/task/${taskId}`;

        const data = {
          store: true,
          invited_id: this.practitionerID,
        };

        await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
          .then((response) => response.json())
          .then((res_data) => {
            this.isAwaitingResponse = false;
            if (res_data.status == 'ok') {
              const { message, data, status } = res_data;

              this.removeURLParam('patient_success');

              this.showNotification(
                {
                  message,
                  status,
                },
                10000
              );
              if (data) {
                this.patients = JSON.parse(data);
                this.patients.forEach((patient) => {
                  patient.register_date = this.normalizeDate(
                    patient.register_date
                  );
                });
              }
            }

            if (res_data.status == 'warning' || res_data.status == 'error') {
              const { message, status } = res_data;

              this.showNotification(
                {
                  message,
                  status,
                },
                10000
              );
            }
          })
          .catch((error) => {
            this.isAwaitingResponse = false;
            this.showNotification({
              message:
                'Something went wrong. More information in the developer console',
              status: 'error',
            });
            console.error('Error:', error);
          });
      }
    },

    flattenMetaobject(node) {
      const fieldData = {};
      if (node.fields) {
        node.fields.forEach(f => {
          fieldData[f.key] = f.value;
        });
      }
      return {
        system_id: node.id,
        title: fieldData.title || 'Untitled',
        raw_date: fieldData.created || '',
        created: fieldData.created ? this.normalizeDate(fieldData.created) : '',
        note: fieldData.note || '',
        products: typeof fieldData.products === 'string' ? JSON.parse(fieldData.products) : (fieldData.products || []),
        created_by: fieldData.created_by || ''
      };
    },
    normalizePatients(data) {
      if (Array.isArray(data)) {
        return data;
      }
      if (data?.references?.nodes) {
        return data.references.nodes.map(node =>
          this.flattenMetaobject(node)
        );
      }
      console.warn('Unknown patients format:', data);
      return [];
    },
    safeParseJSON(data) {
      if (!data) return null;

      if (typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch (e) {
          console.warn('Invalid JSON string:', data);
          return null;
        }
      }
      if (typeof data === 'object') {
        return data;
      }
      return null;
    },
    editViewItem(systemID, customerID) {
      const protocolID = String(systemID);

      const protocol = this.patients.find(
        p => String(p.system_id) === protocolID
      );

      if (!protocol) {
        console.warn('Protocol not found:', protocolID);
        return;
      }

      let products = [];

      if (
        Array.isArray(protocol.products) &&
        protocol.products.length &&
        typeof protocol.products[0] === 'object'
      ) {
        products = protocol.products;
      }

      if (
        !products.length &&
        this.copiedProtocolProductsMap[protocolID]
      ) {
        products = this.copiedProtocolProductsMap[protocolID];
      }

      const cleanID = protocolID.split('/').pop();

      const storageData = {
        body: {
          protocol_id: `gid://shopify/Metaobject/${cleanID}`,
          customer_id: String(customerID),
          title: protocol.title || '',
          note: protocol.note || '',
          products: products.map(p => ({
            productId: String(p.productId),
            variantId: String(p.variantId),
            title: p.title || '',
            vendor: p.vendor || '',
            image: p.image || '',
            price: Number(p.price || 0),
            qty: Number(p.qty || 1),
            inventorymax: String(p.inventorymax || ''),
            note: p.note || ''
          }))
        }
      };

      localStorage.setItem(
        'product_recommend__add_new',
        JSON.stringify(storageData)
      );
    },

    async removePatient(systemID, customerID) {
      this.isAwaitingResponse = true;

      const taskId = this.deleteScriptIDinDJ;
      const url = `/apps/datajet/task/${taskId}`; 

      const cleanID = String(systemID).split('/').pop();

      const requestData = {
        store: true,
        protocol_id: `gid://shopify/Metaobject/${cleanID}`,
        customer_id: customerID,
        invited_id: this.practitionerID,
      };

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData),
        });

        const res_data = await response.json();
        this.isAwaitingResponse = false;

        if (res_data.status === 'success' || res_data.status === 'ok') {
          this.showNotification({ message: res_data.message, status: 'ok' });
          this.patients = this.patients.filter(
            item => item.system_id !== systemID
          );
          
          // Update localStorage after removing item
          const practitionerID = this.practitionerID?.split('/').pop();
          if (practitionerID) {
            const localStorageKey = `protocol_dashboard_data_${practitionerID}`;
            localStorage.setItem(localStorageKey, JSON.stringify(this.patients));
          }

          if (this.patients.length === 0) {
            this.currentPage = 1;
          } else {
            const totalPages = Math.ceil(this.patients.length / this.itemsPerPage);
            if (this.currentPage > totalPages) {
              this.currentPage = totalPages;
            }
          }

          if (res_data.data) {
            const rawResponse = typeof res_data.data === 'string' ? JSON.parse(res_data.data) : res_data.data;

            if (rawResponse.references && rawResponse.references.nodes) {
              const newItems = rawResponse.references.nodes.map(node => this.flattenMetaobject(node));

              this.patients = newItems;
              
              // Update localStorage again if API returned new data
              if (practitionerID) {
                const localStorageKey = `protocol_dashboard_data_${practitionerID}`;
                localStorage.setItem(localStorageKey, JSON.stringify(this.patients));
              }
              
              const totalPages = Math.ceil(this.patients.length / this.itemsPerPage);

              if (this.currentPage > totalPages) {
                this.currentPage = totalPages > 0 ? totalPages : 1;
              }
            }
          }
        }
      } catch (error) {
        this.isAwaitingResponse = false;
        console.error('Error:', error);
      }
    },

    async copyProtocol(systemID, customerID) {
      this.isAwaitingResponse = true;
      
      const taskId = this.copyScriptIDinDJ;
      const url = `/apps/datajet/task/${taskId}`;
      
      if (!taskId) {
        this.isAwaitingResponse = false;
        this.showNotification({ message: 'Configuration error: Copy script ID is missing', status: 'error' });
        return;
      }

      const cleanSourceID = String(systemID).split('/').pop();

      const requestData = {
        store: true,
        protocol_id: `gid://shopify/Metaobject/${cleanSourceID}`,
        customer_id: customerID,
        invited_id: this.practitionerID,
      };

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData),
        });

        const res_data = await response.json();
        this.isAwaitingResponse = false;

        if (res_data.status === 'success' || res_data.status === 'ok') {
          this.showNotification({ message: res_data.message, status: 'ok' });

          if (res_data.data && res_data.data.copied_item) {
            const copiedNode = res_data.data.copied_item;
            
            // Flatten the new metaobject node
            const newItem = this.flattenMetaobject(copiedNode);
            
            // Add timestamp to mark as recently copied
            newItem._copiedAt = Date.now();
            
            // Clear _copiedAt from all existing items (in case of multiple copies)
            this.patients.forEach(p => delete p._copiedAt);
            
            // Get source protocol to copy products data
            const sourceProtocol = this.patients.find(
              p => String(p.system_id).includes(cleanSourceID)
            );
            
            // Add the new item to the BEGINNING of the list
            this.patients = [newItem, ...this.patients];
            
            // Store products mapping for the copied protocol
            if (sourceProtocol && Array.isArray(sourceProtocol.products)) {
              this.copiedProtocolProductsMap[newItem.system_id] = 
                JSON.parse(JSON.stringify(sourceProtocol.products));
            }
            
            // Update localStorage with new data
            const practitionerID = this.practitionerID?.split('/').pop();
            if (practitionerID) {
              const localStorageKey = `protocol_dashboard_data_${practitionerID}`;
              localStorage.setItem(localStorageKey, JSON.stringify(this.patients));
            }
            
            // Reset to first page to show the new item
            this.currentPage = 1;
            
            // Auto-clear the _copiedAt flag after 30 seconds
            setTimeout(() => {
              delete newItem._copiedAt;
            }, 30000);
          } else {
            // Fallback: if API returns old format, handle it
            console.warn('API returned unexpected format. Expected copied_item in data.');
          }
        }
      } catch (error) {
        this.isAwaitingResponse = false;
        console.error('Error:', error);
        this.showNotification({ message: 'Failed to copy protocol', status: 'error' });
      }
    },

    getInviteDate() {
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const hours = String(currentDate.getHours()).padStart(2, '0');
      const minutes = String(currentDate.getMinutes()).padStart(2, '0');
      const seconds = String(currentDate.getSeconds()).padStart(2, '0');

      const timezoneOffset = currentDate.getTimezoneOffset();
      const timezoneHours = String(
        Math.floor(Math.abs(timezoneOffset) / 60)
      ).padStart(2, '0');
      const timezoneMinutes = String(Math.abs(timezoneOffset) % 60).padStart(
        2,
        '0'
      );
      const timezoneSign = timezoneOffset > 0 ? '-' : '+';

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timezoneSign}${timezoneHours}:${timezoneMinutes}`;
    },
  };
}
